import type { ICommandBarItemProps } from '@fluentui/react';
import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { saveIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import { createAIActionBinding, toCopilotAction } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import {
  CopilotChatInputPlaceholder,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProgressRef,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import {
  SupplierComplianceStage,
  TenantProcessStateSupplierCompliance,
} from 'models/copilot/Processes/SupplierCompliance/tenantProcessStateSupplierCompliance';
import {
  SupplierIncidentsStage,
  TenantProcessStateSupplierIncidents,
} from 'models/copilot/Processes/SupplierIncidents/tenantProcessStateSupplierIncidents';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import {
  apiCopilotExecuteProcessSupplierCompliance,
  apiCopilotExecuteProcessSupplierIncidents,
} from 'services/Api/copilotService';
import { ProgressHubProcess } from 'services/SignalR/ProgressHub';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { CopilotSupplierPanelState } from './copilotSupplierPanelState';

// The stage commands the server's steps read once from the request state (PLAN-073 spec 3.6 and 3.7)
export const supplierCommandCreateIncidents = 'createIncidents';
export const supplierCommandSave = 'save';
// the request card's actions (PLAN-CTI-084), sent back as the step's command
const supplierRequestCardActions = ['sendRequest', 'requestLater'];
// the pending-request card under a finished run's result message (PLAN-CTI-098 D20): each starts the check again with
// its command, which resends or cancels the request without a search
const supplierPendingRequestCardActions = ['resendRequest', 'cancelRequest'];
// the files card's actions (PLAN-CTI-092): keep the ticked downloads, or skip them all
const supplierFilesCardActions = ['keepSelectedFiles', 'skipFiles'];
// the location card's actions (PLAN-CTI-092 Addendum 1): check the Asset documents location again, go on without saving,
// or stop the run until later
const supplierLocationCardActions = ['locationRetry', 'locationSkip', 'locationLater'];

/**
 * The two investigations of a Supplier asset (PLAN-073): the incidents and the published compliance, as the Generic
 * usage runs them on the asset's dossier and from an assessment's start card. One execute per turn or stage command, the
 * state kept in the panel's usage state, the concept incidents and the staged documents in the usage preview. The run is
 * the asset's: its id comes from the state, else from the start input (an assessment's card on any scene), else from the
 * dossier. The Generic definition branches to these on its own hooks; the shell stays generic.
 */
export class SupplierProcesses {
  static isSupplierAction(action: CopilotAction | undefined): boolean {
    return (
      action?.processId === TenantProcess.SupplierIncidents || action?.processId === TenantProcess.SupplierCompliance
    );
  }

  static isRunning(runtime: Pick<CopilotPanelRuntimeView, 'action'>): boolean {
    return (
      SupplierProcesses.isSupplierAction(runtime.action) && runtime.action?.status === TenantProcessStatus.InProgress
    );
  }

  static getPanelState(runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>): CopilotSupplierPanelState | undefined {
    return runtime.getUsageState<CopilotSupplierPanelState>(CopilotPanelContext.Supplier);
  }

  // The action a start from an assessment's card runs under on a scene that does not list it: hidden from the lists
  static getFollowedAction(processId: TenantProcess, t: TFunction<string[]>): CopilotAction | undefined {
    if (processId !== TenantProcess.SupplierIncidents && processId !== TenantProcess.SupplierCompliance)
      return undefined;

    const action = toCopilotAction(createAIActionBinding(processStartTemplate, { processId }), t);
    if (action) action.hidden = true;

    return action;
  }

  static getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    return SupplierProcesses.isSupplierAction(action) ? getCopilotProcessWelcomeMessage(action, t) : undefined;
  }

  // The server's stages allow a typed question once the findings or the standards are there; before, nothing is typed.
  static canStartChat(runtime: CopilotPanelRuntimeView): boolean {
    if (!SupplierProcesses.isRunning(runtime)) return false;

    const panelState = SupplierProcesses.getPanelState(runtime);
    if (runtime.action?.processId === TenantProcess.SupplierIncidents) {
      const stage = panelState?.incidents?.stage;

      return stage === SupplierIncidentsStage.Summary || stage === SupplierIncidentsStage.Preview;
    }

    const stage = panelState?.compliance?.stage;

    return (
      stage === SupplierComplianceStage.Summary ||
      stage === SupplierComplianceStage.Review ||
      stage === SupplierComplianceStage.RequestCard ||
      stage === SupplierComplianceStage.FilesCard
    );
  }

  static getChatInputPlaceholder(runtime: CopilotPanelRuntimeView): CopilotChatInputPlaceholder | undefined {
    if (!SupplierProcesses.isRunning(runtime) || SupplierProcesses.canStartChat(runtime)) return undefined;

    return CopilotChatInputPlaceholder.Working;
  }

  // The research, the staging and the checks are the long steps with a bar; they answer in the same request, so a
  // terminal push must not reload the persisted state over the answer. A run restored in such a stage (a reload) has no
  // request to answer in, so its terminal push is the signal to load the state.
  static getProgressRef(
    runtime: CopilotPanelRuntimeView,
    sceneRef: CopilotPanelEntityRef | undefined,
  ): CopilotPanelProgressRef | undefined {
    if (!SupplierProcesses.isRunning(runtime) || !runtime.action) return undefined;

    const isIncidents = runtime.action.processId === TenantProcess.SupplierIncidents;
    const state = SupplierProcesses.getState(runtime, runtime.action);
    const assetId = SupplierProcesses.getAssetId(runtime.action, state, sceneRef);
    if (!assetId) return undefined;

    const working =
      state === undefined ||
      (state instanceof TenantProcessStateSupplierIncidents
        ? state.stage === SupplierIncidentsStage.Investigating
        : state.stage === SupplierComplianceStage.Investigating ||
          state.stage === SupplierComplianceStage.Staging ||
          state.stage === SupplierComplianceStage.Checking);
    if (!working) return undefined;

    return {
      entityType: EntityTypes.Asset,
      entityId: assetId,
      tenantProcess: runtime.action.processId as TenantProcess,
      hubProcess: isIncidents ? ProgressHubProcess.SupplierIncidents : ProgressHubProcess.SupplierCompliance,
      completesInRequest: state === undefined,
    };
  }

  // "Create incidents" over the concept tasks (the Finish); the compliance preview has no command: its files are saved
  static getPreviewCommands(runtime: CopilotPanelRuntime, t: TFunction<string[]>): ICommandBarItemProps[] {
    if (!SupplierProcesses.isRunning(runtime)) return [];

    const panelState = SupplierProcesses.getPanelState(runtime);
    const incidents = panelState?.incidents;
    if (runtime.action?.processId === TenantProcess.SupplierIncidents) {
      if (incidents?.stage !== SupplierIncidentsStage.Preview || incidents.newTasks.length === 0) return [];

      return [
        {
          key: supplierCommandSave,
          text: t('copilot:Supplier.CreateIncidents'),
          iconProps: saveIcon,
          onClick: () => SupplierProcesses.runCommand(runtime, supplierCommandSave),
        },
      ];
    }

    return [];
  }

  /**
   * Whether the usage preview shows a Supplier run: while it runs, and after a compliance run that staged files, whose
   * preview stays as the read-only record of what was saved.
   */
  static showsPreview(runtime: Pick<CopilotPanelRuntimeView, 'action' | 'getUsageState'>): boolean {
    if (SupplierProcesses.isRunning(runtime)) return true;
    if (runtime.action?.processId !== TenantProcess.SupplierCompliance) return false;

    const compliance = SupplierProcesses.getPanelState(runtime)?.compliance;

    return compliance !== undefined && SupplierProcesses.hasPreview(compliance);
  }

  // A stage command: queued, then the conversation is submitted so the execute of this turn carries it. A submit that
  // fails before the execute must not leave the command for the next typed turn.
  static runCommand(runtime: CopilotPanelRuntime, command: string): void {
    const panelState = SupplierProcesses.getPanelState(runtime);
    if (!panelState) return;

    panelState.queueCommand(command);
    void runtime.submitConversation(runtime.conversation).finally(() => panelState.takeCommand());
  }

  /**
   * One execute: a start, a typed question, the card's answer or a stage command; the incidents' save is the Finish.
   * The answer replaces the panel's state and conversation; the concept incidents or the staged documents open the usage
   * preview. Completed or Failed ends the process in the panel, and the dossier is told the asset changed.
   */
  static async execute(runtime: CopilotPanelRuntime, sceneRef: CopilotPanelEntityRef | undefined): Promise<void> {
    const { action } = runtime;
    if (!action || !SupplierProcesses.isSupplierAction(action)) return;

    const panelState = SupplierProcesses.getPanelState(runtime);
    const state = SupplierProcesses.getState(runtime, action);
    const assetId = SupplierProcesses.getAssetId(action, state, sceneRef);
    if (!assetId) return;

    const command = panelState?.takeCommand();
    const status =
      command === supplierCommandSave ? TenantProcessStatus.Completed : action.status || TenantProcessStatus.NotStarted;
    const consumerTaskId =
      state instanceof TenantProcessStateSupplierCompliance
        ? state.consumerTaskId ?? action.startInput?.consumerTaskId
        : action.startInput?.consumerTaskId;

    runtime.setIsLoading(true);
    const answer =
      action.processId === TenantProcess.SupplierIncidents
        ? await apiCopilotExecuteProcessSupplierIncidents(
            status,
            runtime.conversation,
            assetId,
            consumerTaskId,
            command,
            runtime.connectionId,
            runtime.accessToken,
            runtime.appContext.globalDataCache,
          )
        : await apiCopilotExecuteProcessSupplierCompliance(
            status,
            runtime.conversation,
            assetId,
            consumerTaskId,
            command,
            runtime.connectionId,
            runtime.accessToken,
          );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
    SupplierProcesses.setState(runtime, answer);
    runtime.setPreviewContent(
      SupplierProcesses.hasPreview(answer) ? CopilotPreviewContent.Usage : CopilotPreviewContent.None,
    );

    if (answer.status !== TenantProcessStatus.Completed && answer.status !== TenantProcessStatus.Failed) return;

    await runtime.endProcess(action.processId as TenantProcess);
    // ending the process clears the previews; a compliance run keeps its saved documents as a read-only preview
    if (answer instanceof TenantProcessStateSupplierCompliance && SupplierProcesses.hasPreview(answer)) {
      SupplierProcesses.setState(runtime, answer);
      runtime.setPreviewContent(CopilotPreviewContent.Usage);
    }
    // the incidents are linked to the asset and the documents attached to it: the dossier reloads while it is clean
    runtime.resultBus.publishGeneralCompletion(
      runtime.content?.context ?? CopilotPanelContext.Generic,
      EntityTypes.Asset,
      assetId,
    );
  }

  /**
   * The incidents card is this process's own: its choices (the template, the ticked findings, the owner) go onto the
   * block of this runtime's conversation, where the server reads them; a user bubble words the request and the panel runs
   * the execute with the command. Resend and Cancel of the pending-request card start a finished check again instead.
   */
  static onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
    t: TFunction<string[]>,
  ): boolean {
    const action = payload.actionData.action;
    if (typeof action === 'string' && supplierPendingRequestCardActions.includes(action)) {
      SupplierProcesses.startWithCommand(runtime, action);

      return false;
    }

    if (!SupplierProcesses.isRunning(runtime)) return false;

    let wording: string | undefined;
    if (action === supplierCommandCreateIncidents) {
      if (runtime.action?.processId !== TenantProcess.SupplierIncidents) return false;
      wording = t('copilot:Supplier.CreateRequested');
    } else if (
      typeof action === 'string' &&
      [
        ...supplierRequestCardActions,
        ...supplierFilesCardActions,
        ...supplierLocationCardActions,
      ].includes(action)
    ) {
      if (runtime.action?.processId !== TenantProcess.SupplierCompliance) return false;
      wording = SupplierProcesses.getComplianceCardWording(action, payload.inputs, t);
    } else {
      return false;
    }

    // the card's inputs go onto its block, where the server reads them (the request card's recipient)
    for (const threadItem of runtime.conversation.thread) {
      const target = threadItem.contentBlocks?.find((candidate) => candidate.id === block.id);
      if (target) {
        target.adaptiveInputs = { ...(target.adaptiveInputs ?? {}), ...payload.inputs };
        break;
      }
    }

    SupplierProcesses.getPanelState(runtime)?.queueCommand(action);
    const item = new CopilotConversationItem();
    item.type = CopilotConversionItemType.User;
    item.question = wording;
    runtime.conversation.thread.push(item);
    runtime.setConversation(runtime.conversation.clone());

    return true;
  }

  // A finished compliance run's command: queued, then the check starts again and its execute carries the command. A
  // start that fails before the execute must not leave the command for the next turn.
  private static startWithCommand(runtime: CopilotPanelRuntime, command: string): void {
    if (SupplierProcesses.isRunning(runtime) || runtime.action?.processId !== TenantProcess.SupplierCompliance) return;

    const panelState = SupplierProcesses.getPanelState(runtime);
    if (!panelState) return;

    panelState.queueCommand(command);
    void runtime.startActiveProcess().finally(() => panelState.takeCommand());
  }

  // the user bubble of a compliance card's action; a switch over literal keys, as the scanner needs them
  private static getComplianceCardWording(
    action: string,
    inputs: Record<string, string>,
    t: TFunction<string[]>,
  ): string {
    switch (action) {
      case 'sendRequest':
        return t('copilot:Supplier.RequestSent', { recipient: (inputs.recipient ?? '').trim() });
      case 'requestLater':
        return t('copilot:Supplier.RequestLater');
      case 'keepSelectedFiles':
        return t('copilot:Supplier.FilesKeepRequested');
      case 'skipFiles':
        return t('copilot:Supplier.FilesSkipRequested');
      case 'locationRetry':
        return t('copilot:Supplier.LocationRetryRequested');
      case 'locationSkip':
        return t('copilot:Supplier.LocationSkipRequested');
      default:
        return t('copilot:Supplier.LocationLater');
    }
  }

  // A reload of the persisted state: a landing on a run's stage (read-only on the server) or the terminal push
  static applyProcessState(
    runtime: CopilotPanelRuntime,
    state: CopilotPanelProcessState,
  ): CopilotPreviewContent | undefined {
    if (
      !(state.data instanceof TenantProcessStateSupplierIncidents) &&
      !(state.data instanceof TenantProcessStateSupplierCompliance)
    ) {
      return undefined;
    }

    SupplierProcesses.setState(runtime, state.data);
    runtime.setConversation(state.conversation);

    return SupplierProcesses.hasPreview(state.data) ? CopilotPreviewContent.Usage : undefined;
  }

  static clearPreview(runtime: CopilotPanelRuntime): void {
    const panelState = SupplierProcesses.getPanelState(runtime);
    panelState?.setIncidents(undefined);
    panelState?.setCompliance(undefined);
    panelState?.setFileIndex(0);
  }

  private static getState(
    runtime: Pick<CopilotPanelRuntimeView, 'getUsageState'>,
    action: CopilotAction,
  ): TenantProcessStateSupplierIncidents | TenantProcessStateSupplierCompliance | undefined {
    const panelState = SupplierProcesses.getPanelState(runtime);

    return action.processId === TenantProcess.SupplierIncidents ? panelState?.incidents : panelState?.compliance;
  }

  private static setState(
    runtime: CopilotPanelRuntime,
    state: TenantProcessStateSupplierIncidents | TenantProcessStateSupplierCompliance,
  ): void {
    const panelState = SupplierProcesses.getPanelState(runtime);
    if (state instanceof TenantProcessStateSupplierIncidents) {
      panelState?.setIncidents(state);
    } else {
      panelState?.setCompliance(state);
      // the run's own session (the step makes it): the chat's File cards reach the staged files through the panel's
      if (state.sessionId) runtime.setCopilotSessionId(state.sessionId);
    }
  }

  // the run's asset: the state's, else the start input's (an assessment's card on any scene), else the dossier's
  private static getAssetId(
    action: CopilotAction,
    state: TenantProcessStateSupplierIncidents | TenantProcessStateSupplierCompliance | undefined,
    sceneRef: CopilotPanelEntityRef | undefined,
  ): number | undefined {
    if (state?.assetId) return state.assetId;
    if (action.startInput?.entityType === EntityTypes.Asset && action.startInput.entityId) {
      return action.startInput.entityId;
    }

    return sceneRef?.entityType === EntityTypes.Asset && sceneRef.entityId ? sceneRef.entityId : undefined;
  }

  private static hasPreview(
    state: TenantProcessStateSupplierIncidents | TenantProcessStateSupplierCompliance,
  ): boolean {
    if (state instanceof TenantProcessStateSupplierIncidents) {
      return state.stage === SupplierIncidentsStage.Preview && state.newTasks.length > 0;
    }

    // Completed holds the saved files and the files card the ones that wait for the person; Review only on a row from
    // before the files were saved without a choice
    return (
      (state.stage === SupplierComplianceStage.Completed ||
        state.stage === SupplierComplianceStage.FilesCard ||
        state.stage === SupplierComplianceStage.Review) &&
      state.stagedFiles.length > 0
    );
  }
}
